import React, { useEffect, useState } from 'react';
import {
    Box, Typography,
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    Paper, CircularProgress, Alert, useTheme, Chip, IconButton,
    Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField, Tooltip, MenuItem
} from '@mui/material';
import Layout from './Layout';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';

const Siti = () => {
    const [siti, setSiti] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [openModal, setOpenModal] = useState(false);
    const [selectedSito, setSelectedSito] = useState(null);
    const [editForm, setEditForm] = useState({
        nomesito: '',
        codicesito: '',
        indirizzosito: '',
        capsito: '',
        idcomune_fk: ''
    });
    const [openAddModal, setOpenAddModal] = useState(false);
    const [cities, setCities] = useState([]);
    const [newSito, setNewSito] = useState({
        nomesito: '',
        codicesito: '',
        indirizzosito: '',
        capsito: '',
        idcomune_fk: ''
    });
    const theme = useTheme();

    const fetchSiti = () => {
        const token = localStorage.getItem('token');
        fetch('/papi2/sites', {
            method: 'GET',
            headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json',
            },
        })
            .then(response => {
                if (!response.ok) {
                    if (response.status === 401 || response.status === 403) {
                        localStorage.removeItem('token');
                        window.location.href = '/login';
                    }
                    throw new Error('Errore nel caricamento dei siti');
                }
                return response.json();
            })
            .then(data => {
                if (Array.isArray(data)) {
                    setSiti(data);
                } else {
                    console.error('API non ha restituito un array di siti:', data);
                    setSiti([]);
                }
                setLoading(false);
            })
            .catch(err => {
                setError(err.message);
                setLoading(false);
            });
    };

    const handleOpenModal = (sito) => {
        setSelectedSito(sito);
        setEditForm({
            nomesito: sito.nomesito || '',
            codicesito: sito.codicesito || '',
            indirizzosito: sito.indirizzosito || '',
            capsito: sito.capsito || ''
        });
        setOpenModal(true);
    };

    const handleSave = () => {
        const token = localStorage.getItem('token');
        fetch(`/papi2/sites/${selectedSito.idsito}`, {
            method: 'PUT',
            headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json',
            },
            body: JSON.stringify(editForm),
        })
            .then(response => {
                if (!response.ok) throw new Error('Errore durante il salvataggio');
                return response.json();
            })
            .then(() => {
                setOpenModal(false);
                fetchSiti();
            })
            .catch(err => {
                alert(err.message);
            });
    };

    const fetchCities = async () => {
        const token = localStorage.getItem('token');
        try {
            const response = await fetch('/papi2/masterdata/getCities', {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (response.ok) {
                const data = await response.json();
                setCities(data);
            }
        } catch (err) {
            console.error("Error fetching cities:", err);
        }
    };

    const handleOpenAddModal = () => {
        fetchCities();
        setOpenAddModal(true);
    };

    const handleAddSito = () => {
        const token = localStorage.getItem('token');
        fetch('/papi2/sites', {
            method: 'POST',
            headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json',
            },
            body: JSON.stringify(newSito),
        })
            .then(response => {
                if (!response.ok) throw new Error('Errore durante l\'aggiunta del sito');
                return response.json();
            })
            .then(() => {
                setOpenAddModal(false);
                setNewSito({ nomesito: '', codicesito: '', indirizzosito: '', capsito: '', idcomune_fk: '' });
                fetchSiti();
            })
            .catch(err => {
                alert(err.message);
            });
    };

    const handleDeleteSito = (idsito) => {
        if (!window.confirm("Sei sicuro di voler eliminare questo sito?")) return;
        const token = localStorage.getItem('token');
        fetch(`/papi2/sites/${idsito}`, {
            method: 'DELETE',
            headers: {
                'Authorization': `Bearer ${token}`,
            },
        })
            .then(response => {
                if (!response.ok) {
                    return response.text().then(text => { throw new Error(text || 'Errore durante l\'eliminazione'); });
                }
                fetchSiti();
            })
            .catch(err => {
                alert(err.message);
            });
    };

    useEffect(() => {
        fetchSiti();
    }, []);

    return (
        <Layout>
            <Box sx={{ p: 3 }}>
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
                    <Typography variant="h4" sx={{ color: theme.palette.text.primary, fontWeight: 'bold' }}>
                        Gestione Siti
                    </Typography>
                    <Button
                        variant="contained"
                        color="primary"
                        startIcon={<AddIcon />}
                        onClick={handleOpenAddModal}
                        sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 700 }}
                    >
                        Nuovo Sito
                    </Button>
                </Box>

                {loading ? (
                    <Box display="flex" justifyContent="center" alignItems="center" minHeight="50vh">
                        <CircularProgress />
                    </Box>
                ) : error ? (
                    <Alert severity="error">{error}</Alert>
                ) : (
                    <TableContainer component={Paper} sx={{ bgcolor: theme.palette.background.paper, boxShadow: 3, borderRadius: 2 }}>
                        <Table>
                            <TableHead>
                                <TableRow sx={{ bgcolor: theme.palette.action.hover }}>
                                    <TableCell sx={{ fontWeight: 'bold' }}>Nome Sito</TableCell>
                                    <TableCell sx={{ fontWeight: 'bold' }}>Codice</TableCell>
                                    <TableCell sx={{ fontWeight: 'bold' }}>Indirizzo</TableCell>
                                    <TableCell sx={{ fontWeight: 'bold' }}>CAP</TableCell>
                                    <TableCell sx={{ fontWeight: 'bold' }}>Comune</TableCell>
                                    <TableCell sx={{ fontWeight: 'bold' }}>Stato</TableCell>
                                    <TableCell sx={{ fontWeight: 'bold' }} align="center">Azioni</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {siti.map((sito) => (
                                    <TableRow key={sito.idsito} hover>
                                        <TableCell sx={{ fontWeight: 600 }}>{sito.nomesito}</TableCell>
                                        <TableCell>{sito.codicesito}</TableCell>
                                        <TableCell>{sito.indirizzosito}</TableCell>
                                        <TableCell>{sito.capsito}</TableCell>
                                        <TableCell>{sito.nomecomune}</TableCell>
                                        <TableCell>
                                            <Chip
                                                label={sito.statosito || 'Attivo'}
                                                size="small"
                                                color={sito.statosito === 'Inattivo' ? 'error' : 'success'}
                                            />
                                        </TableCell>
                                        <TableCell align="center">
                                            <Box display="flex" justifyContent="center" gap={1}>
                                                <Tooltip title="Modifica">
                                                    <IconButton onClick={() => handleOpenModal(sito)} color="primary" size="small">
                                                        <EditIcon />
                                                    </IconButton>
                                                </Tooltip>
                                                {(!sito.num_schede || sito.num_schede === 0) && (
                                                    <Tooltip title="Elimina">
                                                        <IconButton onClick={() => handleDeleteSito(sito.idsito)} color="error" size="small">
                                                            <DeleteIcon />
                                                        </IconButton>
                                                    </Tooltip>
                                                )}
                                            </Box>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                )}

                {/* Edit Modal */}
                <Dialog open={openModal} onClose={() => setOpenModal(false)} maxWidth="sm" fullWidth>
                    <DialogTitle>Modifica Sito</DialogTitle>
                    <DialogContent>
                        <Box sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
                            <TextField
                                label="Nome Sito"
                                fullWidth
                                variant="outlined"
                                value={editForm.nomesito}
                                onChange={(e) => setEditForm({ ...editForm, nomesito: e.target.value })}
                            />
                            <TextField
                                label="Codice Sito"
                                fullWidth
                                variant="outlined"
                                value={editForm.codicesito}
                                onChange={(e) => setEditForm({ ...editForm, codicesito: e.target.value })}
                            />
                            <TextField
                                label="Indirizzo"
                                fullWidth
                                variant="outlined"
                                value={editForm.indirizzosito}
                                onChange={(e) => setEditForm({ ...editForm, indirizzosito: e.target.value })}
                            />
                            <TextField
                                label="CAP"
                                fullWidth
                                variant="outlined"
                                value={editForm.capsito}
                                onChange={(e) => setEditForm({ ...editForm, capsito: e.target.value })}
                            />
                        </Box>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={() => setOpenModal(false)}>Annulla</Button>
                        <Button onClick={handleSave} variant="contained" color="primary">
                            Salva
                        </Button>
                    </DialogActions>
                </Dialog>

                {/* Add Modal */}
                <Dialog open={openAddModal} onClose={() => setOpenAddModal(false)} maxWidth="sm" fullWidth>
                    <DialogTitle>Nuovo Sito</DialogTitle>
                    <DialogContent>
                        <Box sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
                            <TextField
                                label="Nome Sito"
                                fullWidth
                                variant="outlined"
                                value={newSito.nomesito}
                                onChange={(e) => setNewSito({ ...newSito, nomesito: e.target.value })}
                            />
                            <TextField
                                label="Codice Sito"
                                fullWidth
                                variant="outlined"
                                value={newSito.codicesito}
                                onChange={(e) => setNewSito({ ...newSito, codicesito: e.target.value })}
                            />
                            <TextField
                                label="Indirizzo"
                                fullWidth
                                variant="outlined"
                                value={newSito.indirizzosito}
                                onChange={(e) => setNewSito({ ...newSito, indirizzosito: e.target.value })}
                            />
                            <TextField
                                label="CAP"
                                fullWidth
                                variant="outlined"
                                value={newSito.capsito}
                                onChange={(e) => setNewSito({ ...newSito, capsito: e.target.value })}
                            />
                            <TextField
                                select
                                label="Comune"
                                fullWidth
                                variant="outlined"
                                value={newSito.idcomune_fk}
                                onChange={(e) => setNewSito({ ...newSito, idcomune_fk: e.target.value })}
                            >
                                {cities.map((city) => (
                                    <MenuItem key={city.idcomune} value={city.idcomune}>
                                        {city.nomecomune}
                                    </MenuItem>
                                ))}
                            </TextField>
                        </Box>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={() => setOpenAddModal(false)}>Annulla</Button>
                        <Button onClick={handleAddSito} variant="contained" color="primary">
                            Salva
                        </Button>
                    </DialogActions>
                </Dialog>
            </Box>
        </Layout>
    );
};

export default Siti;
